import { z } from "zod"
import { TranslationKey } from "../i18n/translation-key"

/**
 * Characters that are invisible in an input field but make an otherwise
 * correct address fail validation: soft hyphen and the zero-width family.
 * Mechanics regularly paste addresses from Outlook, Word or a web page, and
 * those pastes bring the invisible characters along for the ride.
 */
const invisibleCharacters = /\u00AD|\u200B|\u200C|\u200D|\u2060|\uFEFF/g

/** Dashes that look identical to "-" but are not the ASCII hyphen. */
const hyphenLookalikes = /[\u2010\u2011\u2012\u2013\u2014\u2212]/g

/** Every kind of whitespace, including the non-breaking space. */
const whitespace = /\s+/g

const mailtoPrefix = /^mailto:/i

/** Outlook copies addresses as `Ann-Charlott Gustafsson <ann@solna.se>`. */
const angleBracketedAddress = /<([^<>]+)>/

/** Catches an unbalanced bracket, where the address itself was not matched. */
const surroundingAngleBrackets = /^<|>$/g

export const sanitizeEmail = (value: string): string => {
  const [, bracketedAddress] = value.match(angleBracketedAddress) ?? []

  return (bracketedAddress ?? value)
    .replace(invisibleCharacters, "")
    .replace(hyphenLookalikes, "-")
    .replace(whitespace, "")
    .replace(mailtoPrefix, "")
    .replace(surroundingAngleBrackets, "")
    .toLowerCase()
}

interface EmailSchemaParams {
  /** Message shown when the address is invalid. */
  message?: string
  /** Message shown when the field is missing altogether. */
  requiredMessage?: string
}

/**
 * Schema for user-entered email addresses. Cleans up paste artefacts before
 * validating, so an address that looks correct to a human is also accepted by
 * the program.
 */
export const emailSchema = ({
  message = "common.validation.invalidEmail" satisfies TranslationKey,
  requiredMessage,
}: EmailSchemaParams = {}) =>
  z
    .string({ error: requiredMessage })
    .transform(sanitizeEmail)
    .pipe(z.string().max(255).email({ message }))

/**
 * Same as {@link emailSchema}, but for fields where the address is optional.
 * A field holding only whitespace counts as empty rather than as invalid.
 */
export const optionalEmailSchema = ({
  message = "common.validation.invalidEmail" satisfies TranslationKey,
  requiredMessage,
}: EmailSchemaParams = {}) =>
  z
    .string({ error: requiredMessage })
    .transform(sanitizeEmail)
    .pipe(z.union([z.literal(""), z.string().max(255).email({ message })]))
